/**
 * ComparisonColumnSkeleton Component
 *
 * Skeleton placeholder that mirrors the ComparisonColumn layout.
 * Accepts an optional `sectionShape` to dynamically match
 * the structure of a loaded sibling column.
 */

import { Skeleton } from '@/components/ui/skeleton'

interface SectionShape {
  readonly id: string
  readonly metricCount: number
}

interface ComparisonColumnSkeletonProps {
  variant: 'blue' | 'purple'
  /** When the other column has loaded, pass its section shapes here
   *  so the skeleton mirrors the real layout. */
  sectionShapes?: readonly SectionShape[]
  className?: string
}

/**
 * Skeleton section with a title and N metric rows.
 */
function SkeletonSection({ metricCount }: { metricCount: number }) {
  return (
    <div>
      {/* Section Title */}
      <Skeleton className="h-3 w-28 mb-3" />

      {/* Metric Rows */}
      <div className="space-y-2">
        {Array.from({ length: metricCount }).map((_, i) => (
          <div key={i} className="flex items-center justify-between py-1.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** Default fallback when no sibling data is available — matches all 14 sections */
const DEFAULT_SHAPES: readonly SectionShape[] = [
  { id: 'vanity', metricCount: 5 },
  { id: 'sentiment', metricCount: 4 },
  { id: 'demographics_gender', metricCount: 3 },
  { id: 'demographics_age', metricCount: 5 },
  { id: 'demographics_ethnicity', metricCount: 4 },
  { id: 'demographics_countries', metricCount: 3 },
  { id: 'psychographics', metricCount: 4 },
  { id: 'follower_tier', metricCount: 5 },
  { id: 'emotions', metricCount: 5 },
  { id: 'languages', metricCount: 3 },
  { id: 'platform', metricCount: 4 },
  { id: 'teams', metricCount: 3 },
  { id: 'hashtags', metricCount: 3 },
  { id: 'quality', metricCount: 5 },
]

/**
 * Skeleton placeholder for a comparison column.
 * When `sectionShapes` is provided (derived from the sibling column),
 * the skeleton matches the real section/metric layout exactly.
 */
export function ComparisonColumnSkeleton({
  variant,
  sectionShapes,
  className = '',
}: ComparisonColumnSkeletonProps) {
  const variantLabel = variant === 'blue' ? 'A' : 'B'
  const shapes = sectionShapes ?? DEFAULT_SHAPES

  return (
    <div className={`bg-v11-surface border border-v11-border rounded-lg p-4 ${className}`}>
      {/* Header */}
      <div className="pb-4 border-b border-v11-border mb-4">
        <div className="flex items-center gap-2">
          {/* Variant Badge */}
          <div className="flex items-center justify-center w-6 h-6 rounded bg-v11-surface-hover text-v11-text-secondary text-xs font-bold">
            {variantLabel}
          </div>

          {/* Name skeleton */}
          <Skeleton className="h-4 w-32 flex-1" />

          {/* Count skeleton */}
          <Skeleton className="h-3 w-12 flex-shrink-0" />
        </div>
      </div>

      {/* Content - Skeleton Sections */}
      <div className="space-y-5">
        {shapes.map((shape) => (
          <SkeletonSection key={shape.id} metricCount={shape.metricCount} />
        ))}
      </div>
    </div>
  )
}

export type { SectionShape }
export default ComparisonColumnSkeleton
